/**
 * montecarlo.ts — Monte Carlo simulation engine.
 *
 * Two-phase model:
 *  Phase 1 (Accumulation): W-2/equity/SE income, 401k contributions, saving surplus.
 *  Phase 2 (Retirement):   Passive income (SS + SE), portfolio draws.
 *
 * Starting assets, income, and expenses come from SimulationParams.profile.
 * Tax calculations delegated to taxes.ts. SS benefit math in social_security.ts.
 */

import type {
  SimulationParams,
  SimulationResult,
  YearBand,
  YearState,
} from './types'
import { computeTaxes, computeRetirementTaxes } from './taxes'
import { incomeForYear, expensesForYear } from './scenarios'
import { annualSSBenefit } from './social_security'
import { compute312Expansion } from './business_investment'
import { computeEventModifiers } from './events'
import {
  DEFAULT_REAL_ESTATE_APPRECIATION,
  RETIREMENT_401K_LIMIT,
  DEFAULT_LIFE_EXPECTANCY_AGE,
  DEFAULT_SS_CLAIM_AGE_USER,
  DEFAULT_SS_CLAIM_AGE_SPOUSE,
  BUSINESS_312_DEFAULT_LOAN_RATE,
  BUSINESS_312_DEFAULT_LOAN_TERM_YEARS,
  BUSINESS_312_DEFAULT_POST_LOAN_YIELD,
  BOX_MULLER_LOG_GUARD,
  P10, P25, P50, P75, P90,
  HALF,
  RE_APPRECIATION_STD,
} from './defaults'

// ─── Pseudo-random normal distribution (Box-Muller) ─────────────────────────

function randomNormal(mean: number, std: number): number {
  // Box-Muller transform
  const u1 = Math.random()
  const u2 = Math.random()
  const z = Math.sqrt(-HALF * Math.log(Math.max(u1, BOX_MULLER_LOG_GUARD))) * Math.cos(HALF * Math.PI * u2)
  return mean + std * z
}

// ─── Percentile computation ───────────────────────────────────────────────────

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0
  const idx = (p / 100) * (sorted.length - 1)
  const lo = Math.floor(idx)
  const hi = Math.ceil(idx)
  if (lo === hi) return sorted[lo]
  return sorted[lo] + (idx - lo) * (sorted[hi] - sorted[lo])
}

function buildBand(yearIndex: number, values: number[], startYear: number): YearBand {
  const sorted = [...values].sort((a, b) => a - b)
  const mean = values.reduce((s, v) => s + v, 0) / values.length
  return {
    year: startYear + yearIndex,
    p10: percentile(sorted, P10),
    p25: percentile(sorted, P25),
    p50: percentile(sorted, P50),
    p75: percentile(sorted, P75),
    p90: percentile(sorted, P90),
    mean,
  }
}

// ─── Single iteration ─────────────────────────────────────────────────────────

function runIteration(
  params: SimulationParams,
  userSSAnnualBase: number,
  spouseSSAnnualBase: number,
): YearState[] {
  let liquid = params.profile.liquidAssets
  let retirement = params.profile.retirementAssets
  // Home equity: track separately, appreciates each year
  let homeEquity = params.profile.homeEquity

  const years: YearState[] = []

  for (let y = 0; y < params.projectionYears; y++) {
    const calendarYear = params.startYear + y
    const isRetired = calendarYear >= params.retirementYear

    // ── Stochastic draws ──────────────────────────────────────────────────────
    const blendedReturnMean =
      params.allocation.equity * params.returns.equityMean +
      params.allocation.bonds * params.returns.bondMean +
      params.allocation.cash * params.returns.cashRate
    const blendedReturnStd = Math.sqrt(
      Math.pow(params.allocation.equity * params.returns.equityStd, HALF) +
      Math.pow(params.allocation.bonds * params.returns.bondStd, HALF),
    )
    const portfolioReturn = randomNormal(blendedReturnMean, blendedReturnStd)
    const inflation = randomNormal(params.inflation.mean, params.inflation.std)
    const reAppreciation = DEFAULT_REAL_ESTATE_APPRECIATION + randomNormal(0, RE_APPRECIATION_STD)
    const inflationFactor = Math.pow(1 + inflation, y)

    // ── Life event modifiers ────────────────────────────────────────────────────
    const baseW2 = params.profile.annualW2Income
    const evMod = computeEventModifiers(params.events ?? [], y, baseW2, inflationFactor)

    // Apply portfolio shock (one-time, e.g. market crash)
    if (evMod.portfolioShockMultiplier !== 1.0) {
      liquid *= evMod.portfolioShockMultiplier
      retirement *= evMod.portfolioShockMultiplier
    }

    // Apply return penalty during recovery years (applied to the per-iteration portfolioReturn draw)
    const effectivePortfolioReturn = portfolioReturn - evMod.returnPenalty

    // Apply one-time liquid delta (windfall, divorce, home down payment)
    liquid += evMod.liquidDelta

    let grossIncome: number
    let ssIncome: number
    let totalTax: number
    let federalTax: number
    let stateTax: number
    let ficaTax: number
    let afterTaxIncome: number
    let totalExpenses: number

    // ── 312 Solutions expansion (both phases — hoisted so years.push can reference it)
    const biz = compute312Expansion(params.businessInvestment, y, inflationFactor)
    let annualSavings: number

    if (!isRetired) {
      // ── ACCUMULATION PHASE ──────────────────────────────────────────────────
      const income = incomeForYear(params, y, evMod)
      ssIncome = 0

      const expenseBase = expensesForYear(params, y)
      totalExpenses = expenseBase.total * inflationFactor + evMod.extraExpense + evMod.extraHousing

      const retirementContrib = Math.min(params.annualRetirementContribution, RETIREMENT_401K_LIMIT)

      // ── 312 Solutions expansion income ────────────────────────────────────────
      // When expansion is enabled (interest-only), the existing $200k building
      // income comes through biz — exclude it from SE income to avoid double-count.
      const bizEnabled = params.businessInvestment.enabled && params.businessInvestment.loanMode === 'interest-only'
      const w2Wages = income.w2Wages
      const seBusinessIncome = bizEnabled ? 0 : income.businessIncome

      // Gross income: W-2 + (SE business if not in biz model) + all biz cash received
      grossIncome = w2Wages + seBusinessIncome + biz.income

      const taxes = computeTaxes(w2Wages, seBusinessIncome, retirementContrib, biz.taxablePassiveIncome)
      federalTax = taxes.federalTax
      stateTax = taxes.stateTax
      ficaTax = taxes.ficaTax
      totalTax = taxes.totalTax

      afterTaxIncome = grossIncome - totalTax
      // Capital deployed reduces savings (goes to LLC, not liquid portfolio)
      // Principal returned (on exit/sale) is a non-taxable lump sum — added to liquid
      // IL extra paydown is additional housing cash outflow
      annualSavings = afterTaxIncome - totalExpenses - biz.capitalDeployed + biz.principalReturned

      // Allocate savings: retirement bucket gets contribution, liquid gets remainder
      const retirementAddition = retirementContrib
      const liquidAddition = Math.max(0, annualSavings - retirementContrib)

      liquid = Math.max(0, liquid + liquidAddition) * (1 + effectivePortfolioReturn)
      retirement = (retirement + retirementAddition) * (1 + effectivePortfolioReturn)

    } else {
      // ── RETIREMENT PHASE ────────────────────────────────────────────────────
      const userAge = calendarYear - params.profile.birthYear
      const spouseAge = params.profile.partnerBirthYear > 0
        ? calendarYear - params.profile.partnerBirthYear
        : 0

      // SS income starts at respective claiming ages; indexed by inflation (SS COLA ≈ CPI)
      const userSSThisYear = userAge >= params.userSS.claimingAge
        ? userSSAnnualBase * inflationFactor
        : 0
      const spouseSSThisYear = spouseAge >= params.spouseSS.claimingAge
        ? spouseSSAnnualBase * inflationFactor
        : 0
      ssIncome = userSSThisYear + spouseSSThisYear

      // Passive business income: when expansion is enabled (interest-only), the
      // existing $200k building flows through biz instead of BUSINESS_INCOME_ANNUAL.
      const bizEnabled = params.businessInvestment.enabled && params.businessInvestment.loanMode === 'interest-only'
      const passiveBusiness = bizEnabled ? 0 : params.profile.annualSelfEmploymentIncome * inflationFactor

      // ── 312 Solutions expansion income (rounds continue into retirement) ─────
      grossIncome = ssIncome + passiveBusiness + biz.income

      // Retirement expenses (inflation-adjusted real dollars)
      totalExpenses = params.postRetirementExpenses * inflationFactor + evMod.extraExpense

      // Taxes on retirement income; biz.taxablePassiveIncome raises AGI without SE tax
      const retTaxes = computeRetirementTaxes(ssIncome, passiveBusiness, biz.taxablePassiveIncome)
      federalTax = retTaxes.federalTax
      stateTax = retTaxes.stateTax
      ficaTax = retTaxes.ficaTax
      totalTax = retTaxes.totalTax

      afterTaxIncome = grossIncome - totalTax
      annualSavings = afterTaxIncome - totalExpenses

      // Net cash flow — positive saves into liquid, negative draws from liquid
      const preLiquid = liquid
      liquid += annualSavings - biz.capitalDeployed + biz.principalReturned

      // If liquid exhausted, draw from retirement accounts (post-59½, no penalty)
      if (liquid < 0) {
        const shortfall = -liquid
        liquid = 0
        retirement = Math.max(0, retirement - shortfall)
        // Note: retirement withdrawal taxes not modeled separately (conservative simplification)
        void preLiquid // suppress unused-var lint
      }

      // Apply investment returns to both buckets
      liquid = Math.max(0, liquid) * (1 + effectivePortfolioReturn)
      retirement = Math.max(0, retirement) * (1 + effectivePortfolioReturn)
    }

    // Home equity appreciation
    homeEquity *= (1 + reAppreciation)

    // Net worth
    const totalNetWorth = liquid + retirement + homeEquity + biz.outstandingPrincipal

    years.push({
      year: calendarYear,
      isRetired,
      grossIncome,
      ssIncome,
      federalTax,
      stateTax,
      ficaTax,
      totalTax,
      afterTaxIncome,
      totalExpenses,
      annualSavings,
      liquidAssets: liquid,
      retirementAssets: retirement,
      homeEquity,
      businessLoanOutstanding: biz.outstandingPrincipal,
      totalNetWorth,
      businessInvestmentIncome: biz.income,
    })
  }

  return years
}

// ─── Aggregate across iterations ──────────────────────────────────────────────

export function runSimulation(params: SimulationParams): SimulationResult {
  // Pre-compute SS annual benefits (real 2024 $) — same for every iteration.
  // SS COLA is modeled per-iteration via inflation draws in runIteration.
  const userSSAnnualBase = annualSSBenefit(
    params.userSS.maxContribYears,
    params.userSS.nearMaxContribYears,
    params.userSS.nearMaxFraction,
    params.userSS.claimingAge,
  )
  const spouseSSAnnualBase = annualSSBenefit(
    params.spouseSS.maxContribYears,
    params.spouseSS.nearMaxContribYears,
    params.spouseSS.nearMaxFraction,
    params.spouseSS.claimingAge,
  )

  const allYears: YearState[][] = []
  for (let i = 0; i < params.iterations; i++) {
    allYears.push(runIteration(params, userSSAnnualBase, spouseSSAnnualBase))
  }

  const n = params.projectionYears

  const netWorthBands: YearBand[] = []
  const liquidBands: YearBand[] = []
  const retirementBands: YearBand[] = []
  const incomeBands: YearBand[] = []
  const savingsBands: YearBand[] = []

  for (let y = 0; y < n; y++) {
    const nwValues = allYears.map(iter => iter[y].totalNetWorth)
    const liqValues = allYears.map(iter => iter[y].liquidAssets)
    const retValues = allYears.map(iter => iter[y].retirementAssets)
    const incValues = allYears.map(iter => iter[y].grossIncome)
    const savValues = allYears.map(iter => iter[y].annualSavings)

    netWorthBands.push(buildBand(y, nwValues, params.startYear))
    liquidBands.push(buildBand(y, liqValues, params.startYear))
    retirementBands.push(buildBand(y, retValues, params.startYear))
    incomeBands.push(buildBand(y, incValues, params.startYear))
    savingsBands.push(buildBand(y, savValues, params.startYear))
  }

  // Success: fraction of iterations where REAL net worth (deflated by mean inflation)
  // ever reaches the real target. retirementTargetNetWorth is expressed in startYear
  // real dollars (HC-CONSTANT-CURRENCY). Model values are nominal so we deflate before
  // comparing. Mean inflation is used as the deflator to keep model internals nominal.
  const realTarget = params.retirementTargetNetWorth
  const inflationMean = params.inflation.mean
  let successCount = 0
  const targetYears: number[] = []

  for (const iter of allYears) {
    const hitYear = iter.findIndex((ys, yearIdx) => {
      // Deflate nominal net worth to startYear real dollars
      const realNetWorth = ys.totalNetWorth / Math.pow(1 + inflationMean, yearIdx)
      return realNetWorth >= realTarget
    })
    if (hitYear !== -1) {
      successCount++
      targetYears.push(hitYear)
    }
  }

  const successProbability = successCount / params.iterations

  let medianRetirementYear: number | null = null
  if (targetYears.length > 0) {
    const sorted = [...targetYears].sort((a, b) => a - b)
    const medianOffset = sorted[Math.floor(sorted.length / HALF)]
    medianRetirementYear = params.startYear + medianOffset
  }

  return {
    netWorthBands,
    liquidBands,
    retirementBands,
    incomeBands,
    savingsBands,
    successProbability,
    medianRetirementYear,
    iterations: params.iterations,
  }
}

/**
 * Build default simulation params from facts.
 * Used as the starting state when the app loads.
 * projectionYears is derived from lifeExpectancy and profile.birthYear once the profile is filled.
 */
/**
 * US national average profile (2026 data where available, otherwise 2025/2024 latest).
 * Sources:
 *   - Income: BLS OES May 2025 median annual wage $65,470 → rounded $66k
 *     Household median (Census 2024, published Sep 2025): $80,610 → $80k
 *   - Liquid savings: Federal Reserve 2025 Survey of Consumer Finances (preliminary): $8,500
 *   - Retirement: Fidelity Q4 2025 average 401k balance ~$135k; Vanguard HMCR 2025 median $38k
 *     Blended median for all working Americans ~$90k (Fed SCF 2022, inflated)
 *   - Home equity: NAR Q1 2026 median home price $419k; avg LTV ~70% → equity ~$126k
 *     Blended across ~65% owners and 35% renters → ~$82k
 *   - Housing cost: Census ACS 2024 median housing costs: owners $2,106/mo, renters $1,406/mo
 *     Blended (65/35): $1,878/mo → $22,500/yr
 *   - Other spending: BLS 2024 Consumer Expenditure Survey total $77,280 minus housing $25,436 → $51,844
 *     Rounded to $52k (excludes housing already captured above)
 *   - 401k contribution: Vanguard HMCR 2025 average contribution rate 7.4% of $66k ≈ $4,900
 *   - FICA 2026: SS wage base $176,100 (IRS Rev Proc 2025-52)
 * HC-NO-PERSONAL-DATA: all values are public statistical averages, not real individuals.
 */
export function nationalAverageProfile() {
  return {
    birthYear: 1985,            // median American worker age ~41 (BLS 2025)
    partnerBirthYear: 0,
    childrenCount: 0,           // default blank; user enters real count
    annualW2Income: 80_000,     // Census 2024 median household income $80,610
    annualEquityComp: 0,
    equityCompYears: 0,
    annualSelfEmploymentIncome: 0,
    liquidAssets: 8_500,        // Fed SCF 2025 preliminary median liquid savings
    retirementAssets: 90_000,   // blended median among those with accounts (Fed SCF)
    homeEquity: 82_000,         // NAR Q1 2026 blended (65% owners at ~$126k equity)
    annualHousingExpense: 22_500,  // ACS 2024 blended median owner+renter ($1,878/mo)
    annualOtherExpenses: 52_000,   // BLS 2024 CEX total minus housing
  }
}

export function defaultParams(): SimulationParams {
  const startYear = 2026
  const lifeExpectancy = DEFAULT_LIFE_EXPECTANCY_AGE
  const profile = nationalAverageProfile()

  return {
    profile,
    incomeEvent: {
      enabled: false,
      year: 3,
      newAnnualIncome: 0,
      newEquityComp: 0,
    },
    allocation: {
      equity: 0.70,
      bonds: 0.20,
      cash: 0.10,
    },
    returns: {
      equityMean: 0.08,
      equityStd: 0.16,
      bondMean: 0.04,
      bondStd: 0.06,
      cashRate: 0.04,
    },
    inflation: {
      mean: 0.03,
      std: 0.01,
    },
    annualRetirementContribution: 5_000,   // Vanguard 2025: avg rate ~7.4% of $66k median wage
    retirementTargetNetWorth: 1_850_000,   // 25× total expenses ($74,500) ≈ $1.85M
    projectionYears: 30,
    iterations: 3_000,
    startYear,
    retirementYear: 2052,                  // born 1985, FRA = 67
    lifeExpectancy,
    userSS: {
      birthYear: 1985,
      claimingAge: DEFAULT_SS_CLAIM_AGE_USER,
      maxContribYears: 15,
      nearMaxContribYears: 5,
      nearMaxFraction: 0.60,
    },
    spouseSS: {
      birthYear: 0,
      claimingAge: DEFAULT_SS_CLAIM_AGE_SPOUSE,
      maxContribYears: 0,
      nearMaxContribYears: 0,
      nearMaxFraction: 0.80,
    },
    postRetirementExpenses: 50_000,
    events: [],
    businessInvestment: {
      enabled: false,
      loanMode: 'interest-only',
      loanRate: BUSINESS_312_DEFAULT_LOAN_RATE,
      loanTermYears: BUSINESS_312_DEFAULT_LOAN_TERM_YEARS,
      exitYearOffset: 15,
      postLoanYieldRate: BUSINESS_312_DEFAULT_POST_LOAN_YIELD,
      roundAmount: 50_000,
      roundFrequency: 3,
      maxRounds: 0,
    },
  }
}
